import React, { FC, useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import SimpleSearchInput, { SearchInpProps } from '~src/shared/form/inputs/SimpleSearchInput';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import useInfiniteScroll from '~src/services/hooks/useInfiniteScroll';
import CheckBox from '~src/shared/form/Checkbox';
import AvatarBox, { AvatarType } from '~src/shared/avatarBox/AvatarBox';

import {
  SearchInpBlock,
  SectionBox,
  OptItem,
  SearchContent,
  ItemTitle,
  ItemPlaceholder,
  ScrollableContainer,
} from './ddSearchContent.sc';
import DdHeaderFooterSaveContent from './DdHeaderFooterSaveContent';

export interface OptionItem {
  name?: string;
  id: string;
  nameKey?: string;
  avatarUrl?: string | null;
  avatarType?: AvatarType;
  disabled?: boolean;
}

interface OwnProps {
  width?: string;
  inactive?: boolean;
  maxHeight?: string;
  defaultOpt?: OptionItem;
  optionsList: OptionItem[] | null;
  loadingSearch: boolean;
  nothingFoundKey: string;
  noMoreItems: boolean;
  isPopoverVisible?: boolean;
  selectedOpt?: OptionItem | OptionItem[];
  multiSelect?: boolean;
  noSearch?: boolean;
  onOptClick(option: string): void;
  applyChecked(ids: string[]): void;
  fetchItems(search?: string, loadMore?: boolean): Promise<unknown>;
}
export type SearchContentProps = OwnProps & Pick<SearchInpProps, 'placeholder'>;

const DdSearchContent: FC<SearchContentProps> = (props) => {
  const [t] = useTranslation();
  const containerRef = useRef<null | HTMLDivElement>(null);
  const [isLoadingMore] = useInfiniteScroll(containerRef, fetchMoreItems);
  const inputValue = useRef<string>('');
  const [checkedOpts, setCheckedOpts] = useState<string[]>(() => {
    if (Array.isArray(props.selectedOpt)) {
      return props.selectedOpt.map((opt) => opt.id);
    }
    return [];
  });

  useEffect(() => {
    if (!props.isPopoverVisible) {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    } else if (props.multiSelect) {
      setCheckedOpts(Array.isArray(props.selectedOpt) ? props.selectedOpt.map((opt) => opt.id) : []);
    }
  }, [props.isPopoverVisible]);

  const onOptClick = ({ currentTarget }: React.MouseEvent) => {
    const itemId = currentTarget.getAttribute('data-id');
    if (itemId) {
      props.onOptClick(itemId);
    } else {
      throw Error('The option id is required to be as an custom attribute of an dom node element!');
    }
  };

  const updateCheckedOpts = (__: boolean, itemId: string) => {
    const itemIndex = checkedOpts.indexOf(itemId);
    setCheckedOpts((items) => {
      const itemsCopy = [...items];
      if (itemIndex !== -1) {
        itemsCopy.splice(itemIndex, 1);
        return itemsCopy;
      }
      return [...itemsCopy, itemId];
    });
  };

  const clearChecked = () => {
    setCheckedOpts([]);
  };

  const onSave = () => props.applyChecked(checkedOpts);

  function fetchMoreItems() {
    return props.fetchItems(inputValue.current, true);
  }

  const onChangeSearchInput = (value: string): Promise<unknown> => {
    if (inputValue.current !== value) {
      inputValue.current = value;
      return props.fetchItems(value);
    } else {
      return Promise.resolve();
    }
  };

  const getOptCurClass = (id?: string): string => {
    if (props.selectedOpt && !props.inactive && !props.multiSelect) {
      const selectedId = (props.selectedOpt as OptionItem).id;
      if (id) {
        return selectedId === id ? 'cur' : '';
      } else {
        return props.defaultOpt && selectedId === props.defaultOpt.id ? 'cur' : '';
      }
    } else if (props.multiSelect) {
      return id && checkedOpts.indexOf(id) !== -1 ? 'checked' : '';
    }
    return '';
  };

  const scrollableContent = (
    <ScrollableContainer ref={containerRef} $maxHeight={props.maxHeight || '345px'}>
      {!props.noSearch && (
        <SearchInpBlock>
          <SimpleSearchInput
            placeholder={props.placeholder}
            onChange={onChangeSearchInput}
            setFocus={props.isPopoverVisible}
            resetSearch={!props.isPopoverVisible}
          />
        </SearchInpBlock>
      )}
      <SearchContent>
        {props.defaultOpt && (
          <SectionBox>
            <OptItem
              data-translation={props.defaultOpt.nameKey}
              data-id={props.defaultOpt.id}
              onClick={onOptClick}
              className={getOptCurClass()}
            >
              <ItemTitle>
                {props.defaultOpt.nameKey ? t(props.defaultOpt.nameKey) : props.defaultOpt.name}
              </ItemTitle>
            </OptItem>
          </SectionBox>
        )}
        {(props.loadingSearch || props.optionsList) && (
          <SectionBox>
            {props.loadingSearch && !isLoadingMore ? (
              <LoadingSpinner size={10} alignCls="center-middle" />
            ) : (
              <>
                {props.optionsList!.length ? (
                  props.optionsList!.map((item) => (
                    <OptItem
                      key={item.id}
                      className={getOptCurClass(item.id)}
                      $multiSelect={!!props.multiSelect}
                      $disabled={item.disabled}
                    >
                      {item.avatarType && (
                        <div className="avatar-box">
                          <AvatarBox size={35} url={item.avatarUrl} name={item.name} type={item.avatarType} />
                        </div>
                      )}
                      {props.multiSelect ? (
                        <CheckBox
                          onChange={updateCheckedOpts}
                          id={item.id}
                          val={item.id}
                          labelKey={item.nameKey}
                          label={item.name}
                          isChecked={checkedOpts.indexOf(item.id) !== -1}
                        />
                      ) : (
                        <ItemTitle
                          data-id={item.id}
                          onClick={onOptClick}
                          data-translation={item.nameKey || 'from BE'}
                          $withAvatar={!!item.avatarUrl}
                        >
                          {item.name}
                        </ItemTitle>
                      )}
                    </OptItem>
                  ))
                ) : (
                  <ItemPlaceholder data-translation={props.nothingFoundKey}>
                    {t(props.nothingFoundKey)}
                  </ItemPlaceholder>
                )}
                {isLoadingMore && !props.noMoreItems && (
                  <ItemPlaceholder>
                    <LoadingSpinner size={10} />
                  </ItemPlaceholder>
                )}
              </>
            )}
          </SectionBox>
        )}
      </SearchContent>
    </ScrollableContainer>
  );

  return props.multiSelect ? (
    <DdHeaderFooterSaveContent
      selectionInfo={`${checkedOpts.length} selected`}
      isDisplayClear={!!checkedOpts.length}
      onClickClear={clearChecked}
      onClickSave={onSave}
    >
      {scrollableContent}
    </DdHeaderFooterSaveContent>
  ) : (
    scrollableContent
  );
};

export default DdSearchContent;
